import { format as formatDate, parse } from 'date-fns';

export const extractTimeFormat = (formatString) => {
  if (!formatString) {
    return null;
  }

  const timePattern =
    /\b(HH:mm:ss|HH:mm|hh:mm:ss\s[a][a]?|hh:mm\s[a][a]?|h:mm:ss\s[a][a]?|h:mm\s[a][a]?)\b/g;

  const match = formatString.match(timePattern);
  if (match) {
    return match[0];
  }

  if (/-TIME\b/i.test(formatString)) {
    return 'hh:mm a';
  }

  return null;
};

/** Resolved time-only format for the custom time picker (date-fns tokens). */
export const resolvePickerTimeFormat = (formatString?: string | null) =>
  extractTimeFormat(formatString ?? '') ?? 'hh:mm a';

export const getTimePickerColumns = (formatString?: string | null) => {
  const timeFormat =
    extractTimeFormat(formatString ?? '') ??
    (formatString?.includes(':') ? formatString : null) ??
    'hh:mm a';

  return {
    showHours: /HH|h{1,2}(?!h)/.test(timeFormat),
    showMinutes: /mm(?!m)/.test(timeFormat),
    showSeconds: /ss/.test(timeFormat),
  };
};

/** Smallest meeting duration code (ms) → minute step for the time columns (e.g. 300000 → 5). */
export const getTimeStepMsFromDuration = (duration: number[] = []) => {
  const sorted = duration.filter(Number.isFinite).sort((a, b) => a - b);
  if (sorted.length === 0) {
    return 5 * 60 * 1000;
  }
  return sorted[0];
};

export const getTimeStepMinutesFromDuration = (duration: number[] = []) => {
  const stepMs = getTimeStepMsFromDuration(duration);
  const minutes = Math.round(stepMs / 60000);
  return Math.max(1, minutes || 5);
};

export const convertToFormat = (
  timeString,
  format,
  referenceDate: Date = new Date(),
) => {
  if (!timeString) {
    return timeString;
  }

  try {
    if (String(format).includes('a')) {
      return formatDate(
        parse(timeString, format as string, referenceDate),
        format as string,
      );
    }
  } catch {
    // fall through to 24h parse (react-datepicker custom time value)
  }

  try {
    return formatDate(
      parse(timeString, 'HH:mm', referenceDate),
      format as string,
    );
  } catch {
    return timeString;
  }
};

export const convertTo24HourFormat = (
  timeString,
  format,
  referenceDate: Date = new Date(),
) => {
  if (timeString) {
    const date = parse(timeString, format, referenceDate);
    return formatDate(date, 'HH:mm' as string);
  }
  return timeString;
};
